/**
 * Supabase API Service
 * Issue #236: LocalStorage → Supabase PostgreSQL完全移行実装
 * 
 * LocalStorageの関数をSupabase API呼び出しに置き換えるサービス層
 * 既存のコンポーネントとの互換性を保ちながらSupabaseに移行
 */

import { getSupabase } from '../lib/supabase';
import logger from '../utils/logger';
import type { Customer, TreatmentRecord, Sequences } from '../utils/localStorage';
import type { 
  ExtendedReservation, 
  ExtendedCustomer,
  MedicalRecord, 
  MessageDeliverySettings, 
  DeliveryHistory, 
  CustomerMergeCandidate,
  VisitHistory 
} from '../types/salonboard';
import type { StaffShiftData, ExtendedStaffShiftData } from '../types/extended-shift';
import type { TreatmentRecordInput, TreatmentStatus, PhysicalCondition, PaymentMethod } from '../types/treatment';
import { generateCustomerCode } from '../types/salonboard';

// エラーハンドリング用の型定義
export interface ApiResult<T> {
  data: T | null;
  error: string | null;
  success: boolean;
}

// モックデータ用の型定義（anyの代替）
interface MockCustomerData extends Partial<ExtendedCustomer> {
  visitCount?: number;
  totalSpent?: number;
  rank?: 'bronze' | 'silver' | 'gold' | 'platinum';
}

export interface ApiError {
  message: string;
  code?: string;
  details?: Record<string, unknown>;
}

/**
 * Supabase統合APIサービス
 * LocalStorageの関数と同じインターフェースを提供
 */
export class SupabaseApiService {

  /**
   * 型安全なプロパティ取得ヘルパー
   */
  private static safeGetString(obj: Record<string, unknown>, key: string, defaultValue: string = ''): string {
    const value = obj[key];
    return typeof value === 'string' ? value : defaultValue;
  }

  private static safeGetNumber(obj: Record<string, unknown>, key: string, defaultValue: number = 0): number {
    const value = obj[key];
    return typeof value === 'number' ? value : defaultValue;
  }

  private static safeGetBoolean(obj: Record<string, unknown>, key: string, defaultValue: boolean = false): boolean {
    const value = obj[key];
    return typeof value === 'boolean' ? value : defaultValue;
  }

  private static safeGetArray<T>(obj: Record<string, unknown>, key: string, defaultValue: T[] = []): T[] {
    const value = obj[key];
    return Array.isArray(value) ? value as T[] : defaultValue;
  }

  private static safeGetDate(obj: Record<string, unknown>, key: string): Date {
    const value = obj[key];
    if (value instanceof Date) return value;
    if (typeof value === 'string') return new Date(value);
    return new Date();
  }

  /**
   * エラーハンドリング共通関数
   */
  private static handleError(error: unknown, operation: string): ApiError {
    logger.error(`❌ ${operation} failed:`, error);
    
    return {
      message: (error instanceof Error ? error.message : `${operation} failed`),
      code: (error instanceof Error && 'code' in error ? (error as Error & { code: string }).code : 'UNKNOWN_ERROR'),
      details: error as Record<string, unknown>
    };
  }


  // =================================
  // 顧客管理 API
  // =================================

  /**
   * 全顧客データ取得
   * localStorage.getCustomers() の置き換え
   */
  static async getCustomers(): Promise<Customer[]> {
    try {
      const supabase = getSupabase();
      if (!supabase) {
        throw new Error('Supabase client not available - falling back to localStorage');
      }

      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) {
        throw error;
      }

      // Supabaseデータ → LocalStorage互換形式に変換
      return (data || []).map(SupabaseApiService.transformSupabaseCustomer);
    } catch (error) {
      logger.error('❌ Failed to get customers:', error);
      logger.warn('🔄 Falling back to empty data - ensure LocalStorage provider is active');
      return []; // LocalStorageと同じ振る舞い（空配列でUI破綻を防ぐ）
    }
  }

  /**
   * 顧客データ保存
   * localStorage.saveCustomers() の置き換え
   * 注意: この関数は一括置換のため、通常は個別のCRUD操作を使用
   */
  static async saveCustomers(customers: Customer[]): Promise<void> {
    try {
      const supabase = getSupabase();
      if (!supabase) {
        throw new Error('Supabase client not available - falling back to localStorage');
      }

      // 既存データを全削除（危険な操作のため注意）
      const { error: deleteError } = await supabase
        .from('customers')
        .delete()
        .neq('id', 0);

      if (deleteError) {
        throw deleteError;
      }

      // 新データ挿入
      if (customers.length > 0) {
        const supabaseCustomers = customers.map(SupabaseApiService.transformCustomerForSupabase);
        
        const { error: insertError } = await supabase
          .from('customers')
          .insert(supabaseCustomers);

        if (insertError) {
          throw insertError;
        }
      }

      logger.log(`✅ Saved ${customers.length} customers to Supabase`);
    } catch (error) {
      const apiError = SupabaseApiService.handleError(error, 'Save customers');
      throw new Error(apiError.message);
    }
  }

  /**
   * 新規顧客追加
   * localStorage.addCustomer() の置き換え
   */
  static async addCustomer(customerData: Omit<Customer, 'id' | 'createdAt' | 'updatedAt' | 'customerCode' | 'messageSettings' | 'deliveryHistory' | 'mergeHistory' | 'mergeCandidates' | 'customFields'>): Promise<Customer> {
    try {
      const supabase = getSupabase();
      if (!supabase) {
        throw new Error('Supabase client not available - falling back to localStorage');
      }

      // シーケンス番号取得・更新
      const sequences = await SupabaseApiService.getSequences();
      const customerSequence = (sequences.customer || 0) + 1;
      await SupabaseApiService.updateSequence('customer', customerSequence);

      // 新規顧客データ作成
      const newCustomerData = {
        ...SupabaseApiService.transformCustomerForSupabase({
          ...customerData,
          id: 0, // Supabaseで自動生成される
          customerCode: generateCustomerCode(customerSequence),
          messageSettings: {
            email: true,
            myPage: true,
            push: false,
            sms: false
          },
          deliveryHistory: [],
          mergeHistory: {
            mergedFrom: []
          },
          mergeCandidates: [],
          customFields: {
            other1: '',
            other2: '',
            other3: ''
          },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        } as Customer)
      };

      const { data, error } = await supabase
        .from('customers')
        .insert(newCustomerData)
        .select()
        .single();

      if (error) {
        throw error;
      }

      const newCustomer = SupabaseApiService.transformSupabaseCustomer(data);
      logger.log(`✅ Added new customer: ${newCustomer.name} (${newCustomer.customerCode})`);
      
      return newCustomer;
    } catch (error) {
      const apiError = SupabaseApiService.handleError(error, 'Add customer');
      throw new Error(apiError.message);
    }
  }

  /**
   * 顧客情報更新
   * localStorage.updateCustomer() の置き換え
   */
  static async updateCustomer(id: number, updates: Partial<Customer>): Promise<Customer> {
    try {
      const supabase = getSupabase();
      if (!supabase) {
        throw new Error('Supabase client not available - falling back to localStorage');
      }

      // IDでSupabaseの顧客を検索（LocalStorageのidとSupabaseのidが異なる可能性）
      const existingCustomers = await SupabaseApiService.getCustomers();
      const existingCustomer = existingCustomers.find(c => c.id === id);
      
      if (!existingCustomer) {
        throw new Error(`顧客が見つかりません (ID: ${id})`);
      }

      // medicalRecords の特別処理（LocalStorageと同じ振る舞い）
      const { medicalRecords: newMedicalRecords, ...otherUpdates } = updates;
      
      const updatedData: Record<string, unknown> = {
        ...otherUpdates,
        updated_at: new Date().toISOString()
      };

      // medicalRecordsの追加処理
      if (newMedicalRecords) {
        const current = await SupabaseApiService.getCustomers();
        const currentCustomer = current.find(c => c.id === id);
        updatedData.medical_records = [
          ...(currentCustomer?.medicalRecords || []),
          ...newMedicalRecords
        ];
      }

      const { data, error } = await supabase
        .from('customers')
        .update(updatedData)
        .eq('customer_code', existingCustomer.customerCode)
        .select()
        .single();

      if (error) {
        throw error;
      }

      const updatedCustomer = SupabaseApiService.transformSupabaseCustomer(data);
      logger.log(`✅ Updated customer: ${updatedCustomer.name}`);
      
      return updatedCustomer;
    } catch (error) {
      const apiError = SupabaseApiService.handleError(error, 'Update customer');
      throw new Error(apiError.message);
    }
  }

  /**
   * 顧客削除
   * localStorage.deleteCustomer() の置き換え
   */
  static async deleteCustomer(id: number): Promise<void> {
    try {
      const supabase = getSupabase();
      if (!supabase) {
        throw new Error('Supabase client not available - falling back to localStorage');
      }

      // IDでSupabaseの顧客を検索
      const existingCustomers = await SupabaseApiService.getCustomers();
      const existingCustomer = existingCustomers.find(c => c.id === id);
      
      if (!existingCustomer) {
        throw new Error(`顧客が見つかりません (ID: ${id})`);
      }

      const { error } = await supabase
        .from('customers')
        .delete()
        .eq('customer_code', existingCustomer.customerCode);

      if (error) {
        throw error;
      }

      logger.log(`✅ Deleted customer: ${existingCustomer.name}`);
    } catch (error) {
      const apiError = SupabaseApiService.handleError(error, 'Delete customer');
      throw new Error(apiError.message);
    }
  }

  // =================================
  // 予約管理 API
  // =================================

  /**
   * 全予約データ取得
   * localStorage.getReservations() の置き換え
   */
  static async getReservations(): Promise<ExtendedReservation[]> {
    try {
      const supabase = getSupabase();
      if (!supabase) {
        throw new Error('Supabase client not available - falling back to localStorage');
      }

      const { data, error } = await supabase
        .from('reservations')
        .select(`
          *,
          customers (
            customer_code,
            name
          )
        `)
        .order('reservation_date', { ascending: true });

      if (error) {
        throw error;
      }

      // Supabaseデータ → LocalStorage互換形式に変換
      return (data || []).map(SupabaseApiService.transformSupabaseReservation);
    } catch (error) {
      logger.error('❌ Failed to get reservations:', error);
      logger.warn('🔄 Falling back to empty data - ensure LocalStorage provider is active');
      return []; // LocalStorageと同じ振る舞い（空配列でUI破綻を防ぐ）
    }
  }

  /**
   * 予約データ保存
   * localStorage.saveReservations() の置き換え
   */
  static async saveReservations(reservations: ExtendedReservation[]): Promise<void> {
    try {
      const supabase = getSupabase();
      if (!supabase) {
        throw new Error('Supabase client not available - falling back to localStorage');
      }

      // 一括置換処理（注意深く実行）
      const { error: deleteError } = await supabase
        .from('reservations')
        .delete()
        .neq('id', 0);

      if (deleteError) {
        throw deleteError;
      }

      if (reservations.length > 0) {
        const supabaseReservations = reservations
          .map(r => SupabaseApiService.transformReservationForSupabase(r))
          .filter(r => r !== null);

        if (supabaseReservations.length > 0) {
          const { error: insertError } = await supabase
            .from('reservations')
            .insert(supabaseReservations);

          if (insertError) {
            throw insertError;
          }
        }
      }

      logger.log(`✅ Saved ${reservations.length} reservations to Supabase`);
    } catch (error) {
      const apiError = SupabaseApiService.handleError(error, 'Save reservations');
      throw new Error(apiError.message);
    }
  }

  // =================================
  // 施術記録管理 API
  // =================================

  /**
   * 全施術記録取得
   * localStorage.getTreatments() の置き換え
   */
  static async getTreatments(): Promise<TreatmentRecord[]> {
    try {
      const supabase = getSupabase();
      if (!supabase) {
        throw new Error('Supabase client not available - falling back to localStorage');
      }

      const { data, error } = await supabase
        .from('treatments')
        .select('*')
        .order('treatment_date', { ascending: false });

      if (error) {
        throw error;
      }

      return (data || []).map(SupabaseApiService.transformSupabaseTreatment);
    } catch (error) {
      logger.error('❌ Failed to get treatments:', error);
      logger.warn('🔄 Falling back to empty data - ensure LocalStorage provider is active');
      return []; // LocalStorageと同じ振る舞い（空配列でUI破綻を防ぐ）
    }
  }

  /**
   * 施術記録保存
   * localStorage.saveTreatments() の置き換え
   */
  static async saveTreatments(treatments: TreatmentRecord[]): Promise<void> {
    try {
      const supabase = getSupabase();
      if (!supabase) {
        throw new Error('Supabase client not available - falling back to localStorage');
      }

      // 一括置換処理
      const { error: deleteError } = await supabase
        .from('treatments')
        .delete()
        .neq('id', 0);

      if (deleteError) {
        throw deleteError;
      }

      if (treatments.length > 0) {
        const supabaseTreatments = treatments.map(SupabaseApiService.transformTreatmentForSupabase);

        const { error: insertError } = await supabase
          .from('treatments')
          .insert(supabaseTreatments);

        if (insertError) {
          throw insertError;
        }
      }

      logger.log(`✅ Saved ${treatments.length} treatments to Supabase`);
    } catch (error) {
      const apiError = SupabaseApiService.handleError(error, 'Save treatments');
      throw new Error(apiError.message);
    }
  }

  /**
   * 新規施術記録追加
   * localStorage.addTreatment() の置き換え
   */
  static async addTreatment(treatmentData: Omit<TreatmentRecord, 'id' | 'createdAt' | 'updatedAt'>): Promise<TreatmentRecord> {
    try {
      const supabase = getSupabase();
      if (!supabase) {
        throw new Error('Supabase client not available - falling back to localStorage');
      }

      const newTreatmentData = {
        ...SupabaseApiService.transformTreatmentForSupabase({
          ...treatmentData,
          id: '0', // Supabaseで自動生成（文字列ID）
          createdAt: new Date(),
          updatedAt: new Date()
        } as TreatmentRecord)
      };

      const { data, error } = await supabase
        .from('treatments')
        .insert(newTreatmentData)
        .select()
        .single();

      if (error) {
        throw error;
      }

      const newTreatment = SupabaseApiService.transformSupabaseTreatment(data);

      // 顧客の来店履歴も更新（LocalStorageと同じ振る舞い）
      await SupabaseApiService.updateCustomerAfterTreatment(treatmentData.customerId, newTreatment);

      logger.log(`✅ Added new treatment for customer ID: ${treatmentData.customerId}`);
      
      return newTreatment;
    } catch (error) {
      const apiError = SupabaseApiService.handleError(error, 'Add treatment');
      throw new Error(apiError.message);
    }
  }

  /**
   * 施術記録更新
   * localStorage.updateTreatment() の置き換え
   */
  static async updateTreatment(id: number, updates: Partial<TreatmentRecord>): Promise<TreatmentRecord> {
    try {
      const supabase = getSupabase();
      if (!supabase) {
        throw new Error('Supabase client not available - falling back to localStorage');
      }

      const updatedData = {
        ...updates,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('treatments')
        .update(updatedData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      const updatedTreatment = SupabaseApiService.transformSupabaseTreatment(data);
      logger.log(`✅ Updated treatment ID: ${id}`);
      
      return updatedTreatment;
    } catch (error) {
      const apiError = SupabaseApiService.handleError(error, 'Update treatment');
      throw new Error(apiError.message);
    }
  }

  // =================================
  // シーケンス管理 API
  // =================================

  /**
   * シーケンス番号取得
   * localStorage.getSequences() の置き換え
   */
  static async getSequences(): Promise<Sequences> {
    try {
      const supabase = getSupabase();
      if (!supabase) {
        throw new Error('Supabase client not available - falling back to localStorage');
      }

      const { data, error } = await supabase
        .from('sequences')
        .select('*');

      if (error) {
        throw error;
      }

      // 配列をオブジェクトに変換
      const sequences: Sequences = {};
      (data || []).forEach((seq: { sequence_name: string; current_value: number }) => {
        sequences[seq.sequence_name as keyof Sequences] = seq.current_value;
      });

      return sequences;
    } catch (error) {
      logger.error('❌ Failed to get sequences:', error);
      return {};
    }
  }

  /**
   * シーケンス番号更新
   * localStorage.updateSequence() の置き換え
   */
  static async updateSequence(key: keyof Sequences, value: number): Promise<void> {
    try {
      const supabase = getSupabase();
      if (!supabase) {
        throw new Error('Supabase client not available - falling back to localStorage');
      }

      const { error } = await supabase
        .from('sequences')
        .upsert({
          sequence_name: key,
          current_value: value,
          updated_at: new Date().toISOString()
        });

      if (error) {
        throw error;
      }

      logger.log(`✅ Updated sequence ${key}: ${value}`);
    } catch (error) {
      const apiError = SupabaseApiService.handleError(error, 'Update sequence');
      throw new Error(apiError.message);
    }
  }

  // =================================
  // スタッフシフト管理 API
  // =================================
  // NOTE: staff_shiftsテーブルは将来的に追加予定
  // 現在はLocalStorageフォールバック機能に依存

  /**
   * スタッフシフト一覧を取得
   */
  static async getStaffShifts(): Promise<StaffShiftData[]> {
    try {
      // TODO: staff_shiftsテーブル実装後にSupabase実装を追加
      logger.warn('⚠️ Staff shifts Supabase table not implemented yet - falling back to localStorage');
      throw new Error('Staff shifts Supabase integration not implemented - will use localStorage fallback');
    } catch (error) {
      const apiError = SupabaseApiService.handleError(error, 'Get staff shifts');
      throw new Error(apiError.message);
    }
  }

  /**
   * スタッフシフト一括保存
   */
  static async saveStaffShifts(_shifts: StaffShiftData[]): Promise<void> {
    try {
      // TODO: staff_shiftsテーブル実装後にSupabase実装を追加
      logger.warn('⚠️ Staff shifts Supabase table not implemented yet - falling back to localStorage');
      throw new Error('Staff shifts Supabase integration not implemented - will use localStorage fallback');
    } catch (error) {
      const apiError = SupabaseApiService.handleError(error, 'Save staff shifts');
      throw new Error(apiError.message);
    }
  }

  /**
   * 新しいスタッフシフト追加
   */
  static async addStaffShift(_shiftData: Omit<StaffShiftData, 'id' | 'createdAt' | 'updatedAt'>): Promise<StaffShiftData> {
    try {
      // TODO: staff_shiftsテーブル実装後にSupabase実装を追加
      logger.warn('⚠️ Staff shifts Supabase table not implemented yet - falling back to localStorage');
      throw new Error('Staff shifts Supabase integration not implemented - will use localStorage fallback');
    } catch (error) {
      const apiError = SupabaseApiService.handleError(error, 'Add staff shift');
      throw new Error(apiError.message);
    }
  }

  /**
   * スタッフシフト更新
   */
  static async updateStaffShift(_id: string, _updates: Partial<StaffShiftData>): Promise<StaffShiftData> {
    try {
      // TODO: staff_shiftsテーブル実装後にSupabase実装を追加
      logger.warn('⚠️ Staff shifts Supabase table not implemented yet - falling back to localStorage');
      throw new Error('Staff shifts Supabase integration not implemented - will use localStorage fallback');
    } catch (error) {
      const apiError = SupabaseApiService.handleError(error, 'Update staff shift');
      throw new Error(apiError.message);
    }
  }

  /**
   * スタッフシフト削除
   */
  static async deleteStaffShift(_id: string): Promise<void> {
    try {
      // TODO: staff_shiftsテーブル実装後にSupabase実装を追加
      logger.warn('⚠️ Staff shifts Supabase table not implemented yet - falling back to localStorage');
      throw new Error('Staff shifts Supabase integration not implemented - will use localStorage fallback');
    } catch (error) {
      const apiError = SupabaseApiService.handleError(error, 'Delete staff shift');
      throw new Error(apiError.message);
    }
  }

  /**
   * 拡張スタッフシフト一覧を取得
   */
  static async getExtendedStaffShifts(): Promise<ExtendedStaffShiftData[]> {
    try {
      // TODO: staff_shiftsテーブル実装後にSupabase実装を追加
      logger.warn('⚠️ Extended staff shifts Supabase table not implemented yet - falling back to localStorage');
      throw new Error('Extended staff shifts Supabase integration not implemented - will use localStorage fallback');
    } catch (error) {
      const apiError = SupabaseApiService.handleError(error, 'Get extended staff shifts');
      throw new Error(apiError.message);
    }
  }

  /**
   * 拡張スタッフシフト一括保存
   */
  static async saveExtendedStaffShifts(_shifts: ExtendedStaffShiftData[]): Promise<void> {
    try {
      // TODO: staff_shiftsテーブル実装後にSupabase実装を追加
      logger.warn('⚠️ Extended staff shifts Supabase table not implemented yet - falling back to localStorage');
      throw new Error('Extended staff shifts Supabase integration not implemented - will use localStorage fallback');
    } catch (error) {
      const apiError = SupabaseApiService.handleError(error, 'Save extended staff shifts');
      throw new Error(apiError.message);
    }
  }

  // =================================
  // 施術記録 (Treatment Records) - プレースホルダー実装
  // =================================

  /**
   * 指定顧客の全施術記録を取得 (プレースホルダー)
   */
  static async getTreatmentRecords(_customerId: number): Promise<TreatmentRecord[]> {
    try {
      // TODO: treatments_per_customerテーブル実装後にSupabase実装を追加
      logger.warn('⚠️ Treatment records Supabase implementation not ready - falling back to localStorage');
      throw new Error('Treatment records Supabase integration not implemented - will use localStorage fallback');
    } catch (error) {
      const apiError = SupabaseApiService.handleError(error, 'Get treatment records');
      throw new Error(apiError.message);
    }
  }

  /**
   * 指定顧客の最新施術記録を取得 (プレースホルダー)
   */
  static async getLatestTreatmentRecord(_customerId: number): Promise<TreatmentRecord | null> {
    try {
      // TODO: treatments_per_customerテーブル実装後にSupabase実装を追加
      logger.warn('⚠️ Latest treatment record Supabase implementation not ready - falling back to localStorage');
      throw new Error('Latest treatment record Supabase integration not implemented - will use localStorage fallback');
    } catch (error) {
      const apiError = SupabaseApiService.handleError(error, 'Get latest treatment record');
      throw new Error(apiError.message);
    }
  }

  /**
   * 新規施術記録を保存 (プレースホルダー)
   */
  static async saveTreatmentRecord(_customerId: number, _record: TreatmentRecordInput): Promise<TreatmentRecord> {
    try {
      // TODO: treatments_per_customerテーブル実装後にSupabase実装を追加
      logger.warn('⚠️ Save treatment record Supabase implementation not ready - falling back to localStorage');
      throw new Error('Save treatment record Supabase integration not implemented - will use localStorage fallback');
    } catch (error) {
      const apiError = SupabaseApiService.handleError(error, 'Save treatment record');
      throw new Error(apiError.message);
    }
  }

  /**
   * 既存施術記録を更新 (プレースホルダー)
   */
  static async updateTreatmentRecord(_customerId: number, _id: string, _updates: Partial<TreatmentRecord>): Promise<TreatmentRecord | null> {
    try {
      // TODO: treatments_per_customerテーブル実装後にSupabase実装を追加
      logger.warn('⚠️ Update treatment record Supabase implementation not ready - falling back to localStorage');
      throw new Error('Update treatment record Supabase integration not implemented - will use localStorage fallback');
    } catch (error) {
      const apiError = SupabaseApiService.handleError(error, 'Update treatment record');
      throw new Error(apiError.message);
    }
  }

  /**
   * 指定期間の施術記録を取得 (プレースホルダー)
   */
  static async getTreatmentRecordsByDateRange(_customerId: number, _startDate: Date, _endDate: Date): Promise<TreatmentRecord[]> {
    try {
      // TODO: treatments_per_customerテーブル実装後にSupabase実装を追加
      logger.warn('⚠️ Treatment records by date range Supabase implementation not ready - falling back to localStorage');
      throw new Error('Treatment records by date range Supabase integration not implemented - will use localStorage fallback');
    } catch (error) {
      const apiError = SupabaseApiService.handleError(error, 'Get treatment records by date range');
      throw new Error(apiError.message);
    }
  }

  /**
   * 指定顧客の来店回数を取得 (プレースホルダー)
   */
  static async getVisitCount(_customerId: number): Promise<number> {
    try {
      // TODO: treatments_per_customerテーブル実装後にSupabase実装を追加
      logger.warn('⚠️ Visit count Supabase implementation not ready - falling back to localStorage');
      throw new Error('Visit count Supabase integration not implemented - will use localStorage fallback');
    } catch (error) {
      const apiError = SupabaseApiService.handleError(error, 'Get visit count');
      throw new Error(apiError.message);
    }
  }

  /**
   * 施術記録を削除 (プレースホルダー)
   */
  static async deleteTreatmentRecord(_customerId: number, _id: string): Promise<boolean> {
    try {
      // TODO: treatments_per_customerテーブル実装後にSupabase実装を追加
      logger.warn('⚠️ Delete treatment record Supabase implementation not ready - falling back to localStorage');
      throw new Error('Delete treatment record Supabase integration not implemented - will use localStorage fallback');
    } catch (error) {
      const apiError = SupabaseApiService.handleError(error, 'Delete treatment record');
      throw new Error(apiError.message);
    }
  }

  // =================================
  // データ変換関数（プライベート）
  // =================================

  /**
   * Supabaseデータの型ガード
   */

  /**
   * 安全な型変換ヘルパー
   */
  private static safeString(value: unknown, fallback = ''): string {
    return typeof value === 'string' ? value : fallback;
  }

  private static safeNumber(value: unknown, fallback = 0): number {
    return typeof value === 'number' ? value : fallback;
  }

  private static safeArray<T>(value: unknown, fallback: T[] = []): T[] {
    return Array.isArray(value) ? value : fallback;
  }

  private static safeObject<T>(value: unknown, fallback: T): T {
    return (typeof value === 'object' && value !== null) ? value as T : fallback;
  }

  /**
   * Supabase顧客データ → LocalStorage形式変換
   */
  private static transformSupabaseCustomer(supabaseData: Record<string, unknown>): Customer {
    return {
      id: this.safeNumber(supabaseData.id),
      customerCode: this.safeString(supabaseData.customer_code),
      name: this.safeString(supabaseData.name),
      nameKana: this.safeString(supabaseData.name_kana),
      email: this.safeString(supabaseData.email),
      phone: this.safeString(supabaseData.phone),
      birthday: this.safeString(supabaseData.birthday),
      gender: supabaseData.gender as 'male' | 'female' | 'other' | 'unspecified' | undefined,
      occupation: this.safeString(supabaseData.occupation),
      address: this.safeString(supabaseData.address),
      status: supabaseData.status as 'active' | 'inactive',
      rank: supabaseData.rank as 'bronze' | 'silver' | 'gold' | 'platinum',
      totalVisits: this.safeNumber(supabaseData.total_visits),
      totalSpent: parseFloat(this.safeString(supabaseData.total_spent, '0')),
      lastVisit: this.safeString(supabaseData.last_visit),
      medicalRecords: this.safeArray(supabaseData.medical_records, [] as MedicalRecord[]),
      preferences: this.safeString(supabaseData.preferences),
      visitHistory: this.safeArray(supabaseData.visit_history, []),
      messageSettings: this.safeObject(supabaseData.message_settings, {
        email: true,
        myPage: true,
        push: false,
        sms: false
      } as MessageDeliverySettings),
      deliveryHistory: this.safeArray(supabaseData.delivery_history, [] as DeliveryHistory[]),
      mergeHistory: this.safeObject(supabaseData.merge_history, { mergedFrom: [] }),
      mergeCandidates: this.safeArray(supabaseData.merge_candidates, [] as CustomerMergeCandidate[]),
      customFields: this.safeObject(supabaseData.custom_fields, {
        other1: '',
        other2: '',
        other3: ''
      } as Record<string, unknown>),
      // ExtendedCustomerの必須フィールド
      tags: this.safeArray(supabaseData.tags, [] as string[]),
      allergies: this.safeString(supabaseData.allergies),
      skinType: this.safeString(supabaseData.skin_type),
      preferredStaff: this.safeString(supabaseData.preferred_staff),
      medicalInfo: this.safeObject(supabaseData.medical_info, {} as Record<string, unknown>),
      createdAt: this.safeString(supabaseData.created_at),
      updatedAt: this.safeString(supabaseData.updated_at)
    };
  }

  /**
   * LocalStorage顧客データ → Supabase形式変換
   */
  private static transformCustomerForSupabase(customer: Customer): Record<string, unknown> {
    return {
      customer_code: customer.customerCode,
      name: customer.name,
      name_kana: customer.nameKana,
      email: customer.email,
      phone: customer.phone,
      birthday: customer.birthday,
      gender: customer.gender,
      occupation: customer.occupation,
      address: customer.address,
      status: customer.status,
      rank: customer.rank,
      total_visits: customer.totalVisits,
      total_spent: customer.totalSpent,
      last_visit: customer.lastVisit,
      medical_records: customer.medicalRecords,
      preferences: customer.preferences,
      visit_history: customer.visitHistory,
      message_settings: customer.messageSettings,
      delivery_history: customer.deliveryHistory,
      merge_history: customer.mergeHistory,
      merge_candidates: customer.mergeCandidates,
      custom_fields: customer.customFields,
      created_at: customer.createdAt,
      updated_at: customer.updatedAt
    };
  }

  /**
   * Supabase予約データ → LocalStorage形式変換
   */
  private static transformSupabaseReservation(supabaseData: Record<string, unknown>): ExtendedReservation {
    const customers = this.safeObject(supabaseData.customers, {});
    
    return {
      // ExtendedReservationの必須フィールド
      id: this.safeString(supabaseData.id),
      customerId: this.safeNumber(supabaseData.customer_id),
      customerName: this.safeString((customers as Record<string, unknown>).name),
      customerPhone: this.safeString((customers as Record<string, unknown>).phone),
      customerEmail: this.safeString((customers as Record<string, unknown>).email),
      staffId: this.safeString(supabaseData.staff_id),
      staffName: this.safeString(supabaseData.staff_name),
      serviceId: this.safeString(supabaseData.service_id),
      serviceName: Array.isArray(supabaseData.services) ? 
        this.safeString(supabaseData.services[0]) : 
        this.safeString(supabaseData.services),
      date: new Date(this.safeString(supabaseData.reservation_date)),
      startTime: this.safeString(supabaseData.start_time),
      endTime: this.safeString(supabaseData.end_time),
      duration: this.safeNumber(supabaseData.duration),
      status: supabaseData.status as 'confirmed' | 'cancelled' | 'completed',
      notes: this.safeString(supabaseData.notes),
      color: undefined,
      createdAt: new Date(this.safeString(supabaseData.created_at)),
      updatedAt: new Date(this.safeString(supabaseData.updated_at)),
      
      // SalonBoardReservation継承フィールド
      reservationNumber: this.safeString(supabaseData.reservation_number),
      source: 'OTHER' as const,
      detailedStatus: 'WAITING_RECEPTION' as const,
      amount: {
        subtotal: parseFloat(this.safeString(supabaseData.total_amount, '0')),
        tax: 0,
        total: parseFloat(this.safeString(supabaseData.total_amount, '0')),
        discount: 0
      },
      visitProcessing: {
        status: 'PENDING' as const,
        deadline: new Date()
      },
      points: {
        earned: 0,
        used: 0
      },
      cancellationPolicy: {
        type: 'SAME_DAY_50' as const,
        rate: 50
      }
    };
  }

  /**
   * LocalStorage予約データ → Supabase形式変換
   */
  private static transformReservationForSupabase(reservation: ExtendedReservation): Record<string, unknown> | null {
    const customerId = reservation.customerId;
    
    if (!customerId) {
      logger.warn(`❌ Customer not found for reservation ${reservation.id}`);
      return null;
    }

    return {
      customer_id: customerId,
      reservation_date: reservation.date,
      start_time: reservation.startTime,
      end_time: reservation.endTime,
      duration: reservation.duration,
      staff_name: reservation.staffName,
      services: JSON.stringify([reservation.serviceName]),
      status: reservation.status,
      total_amount: reservation.amount?.total || 0,
      payment_status: 'pending',
      notes: reservation.notes,
      internal_notes: reservation.notes || '',
      created_at: reservation.createdAt,
      updated_at: reservation.updatedAt
    };
  }

  /**
   * Supabase施術記録 → LocalStorage形式変換
   */
  private static transformSupabaseTreatment(supabaseData: Record<string, unknown>): TreatmentRecord {
    return {
      id: this.safeGetString(supabaseData, 'id'),
      customerId: this.safeGetNumber(supabaseData, 'customer_id'),
      customerName: this.safeGetString(supabaseData, 'customer_name'),
      date: this.safeGetDate(supabaseData, 'treatment_date'),
      staffId: this.safeGetNumber(supabaseData, 'staff_id'),
      staffName: this.safeGetString(supabaseData, 'staff_name'),
      status: this.safeGetString(supabaseData, 'status', 'completed') as TreatmentStatus,
      treatmentMenu: this.safeGetString(supabaseData, 'treatment_menu'),
      treatmentDuration: this.safeGetNumber(supabaseData, 'treatment_duration', 60),
      techniques: this.safeGetArray<string>(supabaseData, 'techniques'),
      focusAreas: this.safeGetArray<string>(supabaseData, 'focus_areas'),
      physicalCondition: this.safeGetString(supabaseData, 'physical_condition', '普通') as PhysicalCondition,
      symptoms: this.safeGetArray<string>(supabaseData, 'symptoms'),
      lifestyle: supabaseData.lifestyle || {},
      requests: this.safeGetString(supabaseData, 'requests'),
      concerns: this.safeGetString(supabaseData, 'concerns'),
      notes: (supabaseData.notes as { before: string; during: string; after: string; conversation: string }) || { before: '', during: '', after: '', conversation: '' },
      improvements: this.safeGetArray<string>(supabaseData, 'improvements'),
      feedback: this.safeGetString(supabaseData, 'feedback'),
      nextRecommendation: this.safeGetString(supabaseData, 'next_recommendation'),
      nextFocusAreas: this.safeGetArray<string>(supabaseData, 'next_focus_areas'),
      products: this.safeGetArray<{ id: number; name: string; price: number; quantity: number }>(supabaseData, 'products'),
      treatmentAmount: parseFloat(this.safeGetString(supabaseData, 'treatment_amount', '0')),
      productAmount: parseFloat(this.safeGetString(supabaseData, 'product_amount', '0')),
      totalAmount: parseFloat(this.safeGetString(supabaseData, 'total_amount', '0')),
      paymentMethod: this.safeGetString(supabaseData, 'payment_method', 'cash') as PaymentMethod,
      isFirstVisit: this.safeGetBoolean(supabaseData, 'is_first_visit'),
      visitNumber: this.safeGetNumber(supabaseData, 'visit_number', 1),
      createdAt: this.safeGetDate(supabaseData, 'created_at'),
      updatedAt: this.safeGetDate(supabaseData, 'updated_at')
    };
  }

  /**
   * LocalStorage施術記録 → Supabase形式変換
   */
  private static transformTreatmentForSupabase(treatment: TreatmentRecord): Record<string, unknown> {
    return {
      customer_id: treatment.customerId,
      customer_name: treatment.customerName,
      treatment_date: treatment.date,
      staff_id: treatment.staffId,
      staff_name: treatment.staffName,
      status: treatment.status,
      treatment_menu: treatment.treatmentMenu,
      treatment_duration: treatment.treatmentDuration,
      techniques: treatment.techniques,
      focus_areas: treatment.focusAreas,
      physical_condition: treatment.physicalCondition,
      symptoms: treatment.symptoms,
      lifestyle: treatment.lifestyle,
      requests: treatment.requests,
      concerns: treatment.concerns,
      notes: treatment.notes,
      improvements: treatment.improvements,
      feedback: treatment.feedback,
      next_recommendation: treatment.nextRecommendation,
      next_focus_areas: treatment.nextFocusAreas,
      products: treatment.products,
      treatment_amount: treatment.treatmentAmount,
      product_amount: treatment.productAmount,
      total_amount: treatment.totalAmount,
      payment_method: treatment.paymentMethod,
      is_first_visit: treatment.isFirstVisit,
      visit_number: treatment.visitNumber,
      created_at: treatment.createdAt,
      updated_at: treatment.updatedAt
    };
  }

  /**
   * 施術後の顧客情報更新
   * LocalStorageのupdateCustomerAfterTreatment相当
   */
  private static async updateCustomerAfterTreatment(customerId: number, treatment: TreatmentRecord): Promise<void> {
    try {
      const customers = await SupabaseApiService.getCustomers();
      const customer = customers.find(c => c.id === customerId);
      
      if (!customer) {
        logger.warn(`❌ Customer not found for treatment update: ${customerId}`);
        return;
      }

      // 来店履歴更新 - TreatmentRecordをVisitHistory形式に変換
      const visitHistoryEntry: VisitHistory = {
        id: treatment.id,
        date: treatment.date.toISOString(),
        services: [treatment.treatmentMenu],
        staff: treatment.staffName,
        duration: treatment.treatmentDuration,
        reservationId: treatment.reservationId || '',
        treatmentNotes: typeof treatment.notes === 'string' ? treatment.notes : 
                       (typeof treatment.notes === 'object' ? 
                        `${treatment.notes.before || ''} ${treatment.notes.during || ''} ${treatment.notes.after || ''}`.trim() : ''),
        products: Array.isArray(treatment.products) ? 
                 treatment.products.map(p => typeof p === 'string' ? p : p.name || '') : [],
        nextRecommendation: '',
        amount: treatment.totalAmount,
        status: 'completed'
      };
      const updatedVisitHistory = [...(customer.visitHistory || []), visitHistoryEntry];
      
      // 統計情報計算
      const totalVisits = updatedVisitHistory.length;
      const totalSpent = updatedVisitHistory.reduce((sum, visit) => sum + (visit.amount || 0), 0);
      const lastVisit = treatment.date.toISOString();
      
      // ランク計算
      let rank = 'bronze';
      if ((totalSpent as number) >= 300000) {
        rank = 'platinum';
      } else if ((totalSpent as number) >= 150000) {
        rank = 'gold';
      } else if ((totalSpent as number) >= 50000) {
        rank = 'silver';
      }

      // 顧客更新
      await SupabaseApiService.updateCustomer(customerId, {
        visitHistory: updatedVisitHistory,
        totalVisits,
        totalSpent: totalSpent as number,
        lastVisit,
        rank: rank as 'bronze' | 'silver' | 'gold' | 'platinum',
        status: 'active'
      });

    } catch (error) {
      logger.error('❌ Failed to update customer after treatment:', error);
    }
  }

  // =================================
  // ユーティリティ関数
  // =================================

  /**
   * データのクリア（開発用）
   * localStorage.clearAllData() の置き換え
   */
  static async clearAllData(): Promise<void> {
    try {
      const supabase = getSupabase();
      if (!supabase) {
        throw new Error('Supabase client not available - falling back to localStorage');
      }

      const tables = ['treatments', 'reservations', 'customers'];
      
      for (const table of tables) {
        const { error } = await supabase
          .from(table)
          .delete()
          .neq('id', 0);
        
        if (error) {
          logger.warn(`⚠️ Failed to clear ${table}:`, error);
        } else {
          logger.log(`✅ Cleared ${table}`);
        }
      }

      logger.log('✅ All data cleared from Supabase');
    } catch (error) {
      const apiError = SupabaseApiService.handleError(error, 'Clear all data');
      throw new Error(apiError.message);
    }
  }

  /**
   * モックデータでの初期化
   * localStorage.initializeWithMockData() の置き換え
   */
  static async initializeWithMockData(mockCustomers: MockCustomerData[]): Promise<void> {
    try {
      const existingCustomers = await SupabaseApiService.getCustomers();
      
      if (existingCustomers.length === 0) {
        logger.log('🔄 Initializing with mock data...');
        
        for (const mockCustomer of mockCustomers) {
          const customerData = {
            name: mockCustomer.name || '',
            nameKana: mockCustomer.nameKana || '',
            email: mockCustomer.email || '',
            phone: mockCustomer.phone || '',
            birthday: mockCustomer.birthday || '',
            gender: mockCustomer.gender || 'other',
            occupation: mockCustomer.occupation || '',
            address: mockCustomer.address || '',
            status: 'active' as const,
            totalVisits: mockCustomer.visitCount || mockCustomer.totalVisits || 0,
            totalSpent: mockCustomer.totalSpent || 0,
            rank: mockCustomer.rank || 'bronze',
            tags: mockCustomer.tags || [],
            notes: '',
            lastVisit: '',
            preferences: '',
            emergencyContact: {
              name: '',
              phone: '',
              relationship: ''
            }
          };

          await SupabaseApiService.addCustomer(customerData);
        }
        
        logger.log(`✅ Initialized with ${mockCustomers.length} mock customers`);
      } else {
        logger.log('ℹ️ Customers already exist, skipping mock data initialization');
      }
    } catch (error) {
      const apiError = SupabaseApiService.handleError(error, 'Initialize with mock data');
      throw new Error(apiError.message);
    }
  }
}

// =============================================================================
// Settings Management
// =============================================================================

/**
 * Settings取得
 */
export async function getSettings(): Promise<ApiResult<Record<string, unknown>>> {
  try {
    // LocalStorage fallback implementation for settings
    const settingsData = localStorage.getItem('salonhub-settings');
    if (settingsData) {
      return {
        success: true,
        data: JSON.parse(settingsData),
        error: null
      };
    }
    
    return {
      success: true,
      data: null,
      error: null
    };
  } catch (error) {
    logger.error('❌ Failed to get settings:', error);
    return {
      success: false,
      data: null,
      error: error instanceof Error ? error.message : 'Settings取得に失敗しました'
    };
  }
}

/**
 * Settings保存
 */
export async function saveSettings(settings: Record<string, unknown>): Promise<ApiResult<Record<string, unknown>>> {
  try {
    // LocalStorage fallback implementation for settings
    localStorage.setItem('salonhub-settings', JSON.stringify(settings));
    
    return {
      success: true,
      data: settings,
      error: null
    };
  } catch (error) {
    logger.error('❌ Failed to save settings:', error);
    return {
      success: false,
      data: null,
      error: error instanceof Error ? error.message : 'Settings保存に失敗しました'
    };
  }
}

/**
 * Settings部分更新
 */
export async function updateSettingsSection(section: string, data: Record<string, unknown>): Promise<ApiResult<Record<string, unknown>>> {
  try {
    // Get current settings
    const currentResult = await getSettings();
    if (!currentResult.success) {
      return currentResult;
    }
    
    // Update specific section
    const updatedSettings = {
      ...(currentResult.data || {}),
      [section]: {
        ...(currentResult.data?.[section] as Record<string, unknown> || {}),
        ...data
      },
      lastUpdated: new Date().toISOString(),
      version: '1.0.0'
    };
    
    // Save updated settings
    return await saveSettings(updatedSettings);
  } catch (error) {
    logger.error('❌ Failed to update settings section:', error);
    return {
      success: false,
      data: null,
      error: error instanceof Error ? error.message : 'Settings部分更新に失敗しました'
    };
  }
}

/**
 * Settingsエクスポート
 */
export async function exportSettings(): Promise<ApiResult<Record<string, unknown>>> {
  try {
    const result = await getSettings();
    if (!result.success) {
      return result;
    }
    
    return {
      success: true,
      data: {
        settings: result.data,
        exportedAt: new Date().toISOString(),
        version: '1.0.0'
      },
      error: null
    };
  } catch (error) {
    logger.error('❌ Failed to export settings:', error);
    return {
      success: false,
      data: null,
      error: error instanceof Error ? error.message : 'Settingsエクスポートに失敗しました'
    };
  }
}

/**
 * Settingsバックアップ
 */
export async function backupSettings(settings: Record<string, unknown>): Promise<ApiResult<Record<string, unknown>>> {
  try {
    const backup = {
      ...settings,
      backedUpAt: new Date().toISOString(),
      backupVersion: '1.0.0'
    };
    
    localStorage.setItem('salonhub-settings-backup', JSON.stringify(backup));
    
    return {
      success: true,
      data: backup,
      error: null
    };
  } catch (error) {
    logger.error('❌ Failed to backup settings:', error);
    return {
      success: false,
      data: null,
      error: error instanceof Error ? error.message : 'Settingsバックアップに失敗しました'
    };
  }
}

/**
 * 最新のSettingsバックアップ取得
 */
export async function getLatestSettingsBackup(): Promise<ApiResult<Record<string, unknown>>> {
  try {
    const backupData = localStorage.getItem('salonhub-settings-backup');
    if (backupData) {
      return {
        success: true,
        data: JSON.parse(backupData),
        error: null
      };
    }
    
    return {
      success: true,
      data: null,
      error: null
    };
  } catch (error) {
    logger.error('❌ Failed to get latest settings backup:', error);
    return {
      success: false,
      data: null,
      error: error instanceof Error ? error.message : 'Settingsバックアップ取得に失敗しました'
    };
  }
}

/**
 * Settings変更ログ記録
 */
export async function logSettingsChange(changeLog: Record<string, unknown>): Promise<ApiResult<Record<string, unknown>>> {
  try {
    // Get existing logs
    const existingLogsData = localStorage.getItem('salonhub-settings-changes');
    const existingLogs = existingLogsData ? JSON.parse(existingLogsData) : [];
    
    // Add new log entry
    const newLog = {
      ...changeLog,
      id: Date.now().toString(),
      timestamp: new Date().toISOString()
    };
    
    const updatedLogs = [...existingLogs, newLog];
    
    // Keep only last 100 entries
    if (updatedLogs.length > 100) {
      updatedLogs.splice(0, updatedLogs.length - 100);
    }
    
    localStorage.setItem('salonhub-settings-changes', JSON.stringify(updatedLogs));
    
    return {
      success: true,
      data: newLog,
      error: null
    };
  } catch (error) {
    logger.error('❌ Failed to log settings change:', error);
    return {
      success: false,
      data: null,
      error: error instanceof Error ? error.message : 'Settings変更ログ記録に失敗しました'
    };
  }
}

// LocalStorageサービスと同じインターフェースでエクスポート
export const {
  getCustomers,
  saveCustomers,
  addCustomer,
  updateCustomer,
  deleteCustomer,
  getReservations,
  saveReservations,
  getTreatments,
  saveTreatments,
  addTreatment,
  updateTreatment,
  getSequences,
  updateSequence,
  clearAllData,
  initializeWithMockData,
  // Staff Shifts API
  getStaffShifts,
  saveStaffShifts,
  addStaffShift,
  updateStaffShift,
  deleteStaffShift,
  getExtendedStaffShifts,
  saveExtendedStaffShifts,
  // Treatment Records API
  getTreatmentRecords,
  getLatestTreatmentRecord,
  saveTreatmentRecord,
  updateTreatmentRecord,
  getTreatmentRecordsByDateRange,
  getVisitCount,
  deleteTreatmentRecord
} = SupabaseApiService;

export default SupabaseApiService;